import { useToast, Text, VStack } from '@chakra-ui/react'
import { useCallback } from 'react'
import { formatErrorForToast } from './errorHandler'

/**
 * Custom hook for showing toast notifications with consistent styling and positioning
 */
export function useToastNotification() {
  const toast = useToast()

  const showError = useCallback((error: any) => {
    const errorInfo = formatErrorForToast(error)
    
    // If we have multiple errors, render them as a list
    const renderDescription = () => {
      if (errorInfo.errors && errorInfo.errors.length > 0) {
        return (
          <VStack align="stretch" spacing={1} mt={2}>
            {errorInfo.errors.map((errorMsg, index) => (
              <Text key={index} fontSize="sm">
                • {errorMsg}
              </Text>
            ))}
          </VStack>
        )
      }
      return errorInfo.description || undefined
    }
    
    toast({
      title: errorInfo.title,
      description: renderDescription(),
      status: 'error',
      position: 'top-right',
      duration: 7000, // Longer duration for multiple errors
      isClosable: true,
    })
  }, [toast])

  const showSuccess = useCallback((message: string, description?: string) => {
    toast({
      title: message,
      description: description,
      status: 'success',
      position: 'top-right',
      duration: 5000,
      isClosable: true,
    })
  }, [toast])

  const showWarning = useCallback((message: string, description?: string) => {
    toast({
      title: message,
      description: description,
      status: 'warning',
      position: 'top-right',
      duration: 5000,
      isClosable: true,
    })
  }, [toast])

  const showInfo = useCallback((message: string, description?: string) => {
    toast({
      title: message,
      description: description,
      status: 'info',
      position: 'top-right',
      duration: 5000,
      isClosable: true,
    })
  }, [toast])

  return {
    showError,
    showSuccess,
    showWarning,
    showInfo,
    toast, // Also expose the raw toast function for custom usage
  }
}

